import React, { useEffect, useRef, useState } from 'react';
import { Spin, Alert } from 'antd';

interface YandexMapProps {
  center?: [number, number]; // [latitude, longitude]
  zoom?: number;
  markers?: Array<{
    id: string | number;
    position: [number, number];
    title?: string;
    description?: string;
    color?: string;
  }>;
  onMapClick?: (coordinates: [number, number]) => void;
  onMarkerClick?: (markerId: string | number) => void;
  height?: string | number;
  width?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

declare global {
  interface Window {
    ymaps: any;
  }
}

const YandexMap: React.FC<YandexMapProps> = ({
  center = [55.7558, 37.6176], // Москва по умолчанию
  zoom = 10,
  markers = [],
  onMapClick,
  onMarkerClick,
  height = 400,
  width = '100%',
  className,
  style
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Загрузка Яндекс.Карт API
  useEffect(() => {
    const loadYandexMaps = () => {
      if (window.ymaps) {
        window.ymaps.ready(() => {
          setIsLoading(false);
          // Добавляем небольшую задержку для корректной инициализации
          setTimeout(() => {
            initMap();
          }, 100);
        });
        return;
      }

      const script = document.createElement('script');
      script.src = `https://api-maps.yandex.ru/2.1/?apikey=${process.env.REACT_APP_YANDEX_API_KEY}&lang=ru_RU`;
      script.async = true;
      script.onload = () => {
        window.ymaps.ready(() => {
          setIsLoading(false);
          // Добавляем небольшую задержку для корректной инициализации
          setTimeout(() => {
            initMap();
          }, 100);
        });
      };
      script.onerror = () => {
        setError('Ошибка загрузки Яндекс.Карт');
        setIsLoading(false);
      };
      document.head.appendChild(script);
    };

    loadYandexMaps();

    // Очистка при размонтировании
    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.destroy();
          mapInstanceRef.current = null;
        } catch (error) {
          console.error('Ошибка при уничтожении карты:', error);
        }
      }
    };
  // initMap is defined in the component scope and stable; intentionally leaving empty deps to run once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Инициализация карты
  const initMap = () => {
    if (!mapRef.current || !window.ymaps) return;

    // Проверяем, не инициализирована ли уже карта
    if (mapInstanceRef.current) {
      console.log('Карта уже инициализирована, пропускаем повторную инициализацию');
      return;
    }

    // Проверяем, что элемент карты существует и видим
    if (!mapRef.current.offsetParent) {
      console.log('Элемент карты не видим, откладываем инициализацию');
      return;
    }

    try {
      // Создаем карту
      console.log('🗺️ YandexMap: инициализация карты с центром:', center);
      // Яндекс.Карты ожидают центр в формате [latitude, longitude]
      const map = new window.ymaps.Map(mapRef.current, {
        center: center, // [lat, lng]
        zoom: zoom,
        controls: ['zoomControl', 'fullscreenControl', 'geolocationControl']
      });

      mapInstanceRef.current = map;

      // Добавляем обработчик клика по карте
      if (onMapClick) {
        map.events.add('click', (e: any) => {
          const coords = e.get('coords');
          console.log('🗺️ Клик по карте - исходные координаты от Яндекс.Карт:', coords);
          // Яндекс.Карты возвращают координаты в формате [latitude, longitude]
          // НЕ нужно преобразовывать координаты!
          const convertedCoords: [number, number] = [coords[0], coords[1]]; // [lat, lng]
          console.log('🗺️ Клик по карте - координаты для передачи:', convertedCoords);
          onMapClick(convertedCoords);
        });
      }

      // Добавляем маркеры
      addMarkers(map, markers);

    } catch (err) {
      console.error('Ошибка инициализации карты:', err);
      setError('Ошибка инициализации карты');
    }
  };

  // Добавление маркеров на карту
  const addMarkers = (map: any, markersData: typeof markers) => {
    if (!map || !window.ymaps) return;

    // Очищаем существующие маркеры
    map.geoObjects.removeAll();

    markersData.forEach(marker => {
      console.log('📍 YandexMap: создание маркера с координатами:', marker.position);
      // Яндекс.Карты ожидают координаты в формате [latitude, longitude]
      const placemark = new window.ymaps.Placemark(
        [marker.position[0], marker.position[1]], // [lat, lng]
        {
          balloonContent: marker.description || marker.title,
          hintContent: marker.title
        },
        {
          preset: marker.color ? `islands#${marker.color}DotIcon` : 'islands#blueDotIcon',
          iconColor: marker.color || '#3b82f6'
        }
      );

      // Добавляем обработчик клика по маркеру
      if (onMarkerClick) {
        placemark.events.add('click', () => {
          onMarkerClick(marker.id);
        });
      }

      map.geoObjects.add(placemark);
    });
  };

  // Обновление маркеров при изменении props
  useEffect(() => {
    if (mapInstanceRef.current && !isLoading) {
      addMarkers(mapInstanceRef.current, markers);
    }
    // addMarkers is stable within component scope; markers/isLoading are sufficient deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, isLoading]);

  // Обновление центра карты
  useEffect(() => {
    if (mapInstanceRef.current && !isLoading) {
      mapInstanceRef.current.setCenter(center);
    }
    // initMap not included intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, isLoading]);

  // Обновление зума
  useEffect(() => {
    if (mapInstanceRef.current && !isLoading) {
      mapInstanceRef.current.setZoom(zoom);
    }
    // initMap not included intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, isLoading]);

  // Эффект для инициализации карты после того, как компонент стал видимым
  useEffect(() => {
    if (!isLoading && !mapInstanceRef.current && mapRef.current) {
      // Проверяем, что элемент видим
      const checkVisibility = () => {
        if (mapRef.current && mapRef.current.offsetParent && !mapInstanceRef.current) {
          initMap();
        }
      };

      // Проверяем сразу и через небольшую задержку
      checkVisibility();
      const timeoutId = setTimeout(checkVisibility, 200);

      return () => clearTimeout(timeoutId);
    }
    // initMap is intentionally omitted from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (error) {
    return (
      <Alert
        message="Ошибка загрузки карты"
        description={error}
        type="error"
        showIcon
        style={{ height, width }}
      />
    );
  }

  return (
    <div
      ref={mapRef}
      style={{
        height,
        width,
        ...style
      }}
      className={className}
    >
      {isLoading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            backgroundColor: '#f5f5f5'
          }}
        >
          <Spin size="large" tip="Загрузка карты..." />
        </div>
      )}
    </div>
  );
};

export default YandexMap; 